import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CANALS, useCercaUsuaris, type NivellEscalatDto } from '@/api/activacions';
import {
  TIPUS_DESTI,
  useCircuits,
  useDesaCircuit,
  useEtag,
  useServeisAdmin,
  type TipusDesti,
} from '@/api/administracio';
import type { CircuitAvisos } from '@/api/ajustosAvisos';
import { nomUsuari, useNoms } from '@/api/planificacio';
import type { Canal } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Permis } from '@/permisos/Permis';
import { Cerca, ErrorDesa } from './comu';
import { BarraCanvis } from './marc/canvis';
import { useGuardaCanvis } from './marc/canvisContext';
import { opcionsServei, useEstructura } from './marc/estructura';
import './administracio.css';
import './avisos.css';

const ICONA_CANAL: Record<Canal, NomIcona> = {
  PUSH: 'bell',
  PUSH_CRITICA: 'bell-badge',
  EMAIL: 'envelope',
  TRUCADA: 'phone',
};

/** GRUP no té selector de destí: només s'ofereix si el nivell ja el tenia. */
const destinsDisponibles = (actual: string): TipusDesti[] =>
  TIPUS_DESTI.filter((d) => d !== 'GRUP' || actual === 'GRUP');

const nouNivell = (ordre: number): NivellEscalatDto => ({
  ordre,
  tipusDesti: 'TECNIC_GUARDIA_TITULAR',
  destiId: null,
  esperaSeg: 60,
  canals: ['PUSH', 'TRUCADA', 'PUSH_CRITICA'],
});

const nouCircuit = (): CircuitAvisos => ({
  id: null,
  nom: '',
  perDefecte: false,
  actiu: true,
  serveiId: null,
  nivells: [nouNivell(1)],
});

const reordena = (n: NivellEscalatDto[]) => n.map((x, i) => ({ ...x, ordre: i + 1 }));
const ordenats = (n: NivellEscalatDto[]) => [...n].sort((a, b) => a.ordre - b.ordre);
const igualNivell = (a?: NivellEscalatDto, b?: NivellEscalatDto) =>
  !!a &&
  !!b &&
  a.tipusDesti === b.tipusDesti &&
  (a.destiId ?? null) === (b.destiId ?? null) &&
  a.esperaSeg === b.esperaSeg &&
  a.canals.length === b.canals.length &&
  a.canals.every((c) => b.canals.includes(c));

/** Nombre de canvis respecte de la versió desada (camps i nivells modificats). */
function compteCanvis(a: CircuitAvisos, b: CircuitAvisos): number {
  if (a.id == null) return 1;
  let n = 0;
  if (a.nom !== b.nom) n++;
  if ((a.serveiId ?? null) !== (b.serveiId ?? null)) n++;
  if (a.perDefecte !== b.perDefecte) n++;
  if ((a.actiu ?? true) !== (b.actiu ?? true)) n++;
  const na = ordenats(a.nivells);
  const nb = ordenats(b.nivells);
  const max = Math.max(na.length, nb.length);
  for (let i = 0; i < max; i++) if (!igualNivell(na[i], nb[i])) n++;
  return n;
}

/** Selector de persona per als nivells de tipus USUARI (cerca a /usuaris/cerca). */
function SelectorUsuari({
  destiId,
  etiqueta,
  disabled,
  onTria,
}: {
  destiId?: number | null;
  etiqueta: string;
  disabled?: boolean;
  onTria: (id: number) => void;
}) {
  const { t } = useTranslation('administracio');
  const nom = useNoms();
  const [text, setText] = useState('');
  const [obert, setObert] = useState(false);
  const cerca = useCercaUsuaris(text);
  return (
    <div className="cerca-usuari">
      <input
        className="input"
        aria-label={etiqueta}
        disabled={disabled}
        placeholder={destiId != null ? nom(destiId) : t('circuits.cercaPersona')}
        value={text}
        onFocus={() => setObert(true)}
        onBlur={() => setTimeout(() => setObert(false), 150)}
        onChange={(e) => {
          setText(e.target.value);
          setObert(true);
        }}
      />
      {obert && (cerca.data ?? []).length > 0 ? (
        <ul className="cerca-usuari__llista">
          {(cerca.data ?? []).map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onTria(u.id);
                  setText('');
                  setObert(false);
                }}
              >
                {nomUsuari(u)}
                {u.departament ? <span className="dim">{` · ${u.departament}`}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Editor({
  inicial,
  usos,
  onCreat,
}: {
  inicial: CircuitAvisos;
  usos: number;
  onCreat: (c: CircuitAvisos) => void;
}) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const nom = useNoms();
  const estructura = useEstructura();
  const [base, setBase] = useState<CircuitAvisos>(inicial);
  const [c, setC] = useState<CircuitAvisos>(inicial);
  const { etag } = useEtag('/circuits-escalat', c.id);
  const desa = useDesaCircuit(etag);
  const editable = pot('cai.circuits', c.id == null ? 'ALTA' : 'MODIFICAR');
  const nivells = ordenats(c.nivells);
  const senseCanals = nivells.some((n) => n.canals.length === 0);
  const senseUsuari = nivells.some((n) => n.tipusDesti === 'USUARI' && n.destiId == null);
  const invalid = !c.nom.trim() || senseCanals || senseUsuari;
  const canvis = compteCanvis(c, base);
  // L'espera del darrer nivell no compta: després d'ell ja no s'escala.
  const minutAcumulat = (i: number) =>
    Math.round(nivells.slice(0, i).reduce((s, n) => s + n.esperaSeg, 0) / 60);
  const ultim = nivells.length - 1;

  const canviaNivell = (i: number, p: Partial<NivellEscalatDto>) =>
    setC({ ...c, nivells: nivells.map((n, j) => (j === i ? { ...n, ...p } : n)) });
  const mou = (i: number, d: -1 | 1) => {
    const l = [...nivells];
    const [n] = l.splice(i, 1);
    if (!n) return;
    l.splice(i + d, 0, n);
    setC({ ...c, nivells: reordena(l) });
  };
  const desar = () =>
    desa.mutate(
      { ...c, nivells },
      {
        onSuccess: (desat) => {
          const nou = {
            ...c,
            ...(desat ?? {}),
            nivells: desat?.nivells ?? nivells,
          } as CircuitAvisos;
          setBase(nou);
          setC(nou);
          if (c.id == null && desat?.id != null) onCreat(nou);
        },
      },
    );

  const descripcio = (n: NivellEscalatDto) =>
    n.tipusDesti === 'USUARI' && n.destiId != null
      ? nom(n.destiId)
      : t(`circuits.descDesti.${n.tipusDesti}`);

  const opcionsDesti = (actual: string) =>
    destinsDisponibles(actual).map((d) => ({ valor: d, etiqueta: t(`circuits.desti.${d}`) }));

  return (
    <div className="lf__fitxa" data-circuit={c.id ?? 'nou'}>
      <div className="fitxa-cap">
        <div className="fitxa-cap__titols">
          <span className="fitxa-cap__ruta">{t('titols.circuits')}</span>
          <h2>{c.nom || t('circuits.nou')}</h2>
          <div className="fitxa-cap__badges">
            {c.perDefecte ? (
              <span className="badge badge--ok">
                <Icona nom="star" />
                {t('circuits.marcaDefecte')}
              </span>
            ) : null}
            {c.actiu === false ? (
              <span className="badge badge--gray">{t('comu.inactiu')}</span>
            ) : null}
            {c.id != null ? (
              <span className="badge badge--preventiu">
                {t('avisos.circuits.usos', { count: usos })}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="fitxa-cos">
        <div className="panel">
          <div className="panel__body form">
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Camp etiqueta={t('circuits.nom')}>
                <input
                  className="input"
                  value={c.nom}
                  disabled={!editable}
                  onChange={(e) => setC({ ...c, nom: e.target.value })}
                />
              </Camp>
              <div className="camp">
                <span className="camp__label">{t('circuits.servei')}</span>
                <Desplegable<number | ''>
                  aria-label={t('circuits.servei')}
                  valor={c.serveiId ?? ''}
                  disabled={!editable}
                  opcions={[
                    { valor: '', etiqueta: t('circuits.general') },
                    ...opcionsServei(estructura, { seleccionat: c.serveiId }),
                  ]}
                  onCanvia={(v) => setC({ ...c, serveiId: v === '' ? null : v })}
                />
              </div>
            </div>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <div className="avisos-interruptor">
                <label className="switch-row">
                  <span>
                    <strong>{t('circuits.marcaDefecte')}</strong>
                    <span className="camp__ajuda">{t('avisos.circuits.unSolDefecte')}</span>
                  </span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={c.perDefecte}
                    disabled={!editable}
                    onChange={(e) => setC({ ...c, perDefecte: e.target.checked })}
                  />
                </label>
              </div>
              <div className="avisos-interruptor">
                <label className="switch-row">
                  <span>
                    <strong>{t('comu.actiu')}</strong>
                    <span className="camp__ajuda">{t('avisos.circuits.ajudaActiu')}</span>
                  </span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={c.actiu !== false}
                    disabled={!editable}
                    onChange={(e) => setC({ ...c, actiu: e.target.checked })}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="info-banda avisos-callout">
          <Icona nom="clock" />
          <span>
            {t('avisos.circuits.resumTemps', {
              n: ultim + 1,
              min: minutAcumulat(ultim),
            })}{' '}
            <Link to="/admin/canals">{t('avisos.circuits.ajudaConfirmacio')}</Link>
          </span>
        </div>

        <div className="panel">
          <div className="panel__head">
            <h3>{t('circuits.nivells')}</h3>
          </div>
          <div className="panel__body">
            <ol className="circuit-linia" aria-label={t('circuits.nivells')}>
              {nivells.map((n, i) => (
                <Fragment key={i}>
                  <li className="circuit-nivell" data-nivell={n.ordre}>
                    <span className="circuit-nivell__num" aria-hidden="true">
                      {n.ordre}
                    </span>
                    <div className="circuit-nivell__cos">
                      <div className="circuit-nivell__fila">
                        <div className="circuit-nivell__desti">
                          <Desplegable<string>
                            aria-label={t('circuits.destiNivell', { n: n.ordre })}
                            valor={n.tipusDesti}
                            disabled={!editable}
                            opcions={opcionsDesti(n.tipusDesti)}
                            onCanvia={(v) => canviaNivell(i, { tipusDesti: v, destiId: null })}
                          />
                        </div>
                        {n.tipusDesti === 'USUARI' ? (
                          <div className="circuit-nivell__persona">
                            <SelectorUsuari
                              destiId={n.destiId}
                              disabled={!editable}
                              etiqueta={t('circuits.personaNivell', { n: n.ordre })}
                              onTria={(id) => canviaNivell(i, { destiId: id })}
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className="t-footnote dim">{descripcio(n)}</div>
                      <div className="chips">
                        {CANALS.map((cn) => (
                          <button
                            key={cn}
                            type="button"
                            className="chip-canal"
                            aria-pressed={n.canals.includes(cn)}
                            aria-label={t('avisos.circuits.canalNivell', {
                              canal: tc(`canal.${cn}`),
                              n: n.ordre,
                            })}
                            disabled={!editable}
                            onClick={() =>
                              canviaNivell(i, {
                                canals: n.canals.includes(cn)
                                  ? n.canals.filter((x) => x !== cn)
                                  : [...n.canals, cn],
                              })
                            }
                          >
                            <Icona nom={ICONA_CANAL[cn]} />
                            {tc(`canal.${cn}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="circuit-nivell__lateral">
                      <span className="circuit-nivell__minut">
                        {t('avisos.circuits.minut', { min: minutAcumulat(i) })}
                        {i === ultim ? (
                          <span className="badge badge--gray">{t('avisos.circuits.ultim')}</span>
                        ) : null}
                      </span>
                      {editable ? (
                        <span className="circuit-nivell__accions">
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs btn--icona"
                            aria-label={t('circuits.puja', { n: n.ordre })}
                            title={t('circuits.puja', { n: n.ordre })}
                            disabled={i === 0}
                            onClick={() => mou(i, -1)}
                          >
                            <Icona nom="chevron-up" />
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs btn--icona"
                            aria-label={t('circuits.baixa', { n: n.ordre })}
                            title={t('circuits.baixa', { n: n.ordre })}
                            disabled={i === ultim}
                            onClick={() => mou(i, 1)}
                          >
                            <Icona nom="chevron-down" />
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs btn--icona"
                            aria-label={t('circuits.treu', { n: n.ordre })}
                            title={t('circuits.treu', { n: n.ordre })}
                            disabled={nivells.length === 1}
                            onClick={() =>
                              setC({ ...c, nivells: reordena(nivells.filter((_, j) => j !== i)) })
                            }
                          >
                            <Icona nom="trash" className="c-red" />
                          </button>
                        </span>
                      ) : null}
                    </div>
                  </li>
                  {i < ultim ? (
                    <li className="circuit-espera">
                      <label>
                        {t('avisos.circuits.siNoConfirma')}
                        <input
                          className="input input--curt"
                          type="number"
                          min={1}
                          aria-label={t('circuits.esperaNivell', { n: n.ordre })}
                          disabled={!editable}
                          value={Math.round(n.esperaSeg / 60)}
                          onChange={(e) =>
                            canviaNivell(i, {
                              esperaSeg: Math.max(1, Number(e.target.value) || 1) * 60,
                            })
                          }
                        />
                        {t('avisos.circuits.minutsPassa', { n: n.ordre + 1 })}
                      </label>
                    </li>
                  ) : null}
                </Fragment>
              ))}
            </ol>
            {editable ? (
              <button
                type="button"
                className="btn btn--ghost btn--s mt-8"
                onClick={() => setC({ ...c, nivells: [...nivells, nouNivell(nivells.length + 1)] })}
              >
                <Icona nom="plus" />
                {t('circuits.afegeixNivell')}
              </button>
            ) : null}
            <ErrorDesa error={desa.error} />
          </div>
        </div>
      </div>

      {editable ? (
        <BarraCanvis
          n={canvis}
          nom={c.nom || t('circuits.nou')}
          desant={desa.isPending}
          desactivat={invalid}
          textDesa={c.id == null ? t('avisos.circuits.crea') : undefined}
          onDesa={desar}
          onDescarta={() => setC(base)}
        >
          {!c.nom.trim()
            ? t('avisos.circuits.senseNom')
            : senseCanals
              ? t('circuits.senseCanals')
              : senseUsuari
                ? t('circuits.senseUsuari')
                : null}
        </BarraCanvis>
      ) : null}
    </div>
  );
}

export function W25Circuits() {
  const { t } = useTranslation('administracio');
  const circuits = useCircuits();
  const serveis = useServeisAdmin();
  const guarda = useGuardaCanvis();
  const [sel, setSel] = useState<number | 'nou' | null>(null);
  const [creat, setCreat] = useState<CircuitAvisos | null>(null);
  const [cerca, setCerca] = useState('');
  const [inactius, setInactius] = useState(false);
  const llista = (circuits.data ?? []) as CircuitAvisos[];
  const nomServei = (id?: number | null) => serveis.data?.find((s) => s.id === id)?.nom;
  const q = cerca.trim().toLowerCase();
  const visibles = llista.filter(
    (c) =>
      (inactius || c.actiu !== false || c.id === sel) &&
      (!q || `${c.nom} ${nomServei(c.serveiId) ?? ''}`.toLowerCase().includes(q)),
  );
  const [nou] = useState(nouCircuit);
  const actiu =
    sel === 'nou'
      ? nou
      : (llista.find((c) => c.id === sel) ??
        (creat && creat.id === sel ? creat : null) ??
        llista.find((c) => c.perDefecte) ??
        llista[0]);
  const usos = (c: CircuitAvisos) =>
    (serveis.data ?? []).filter(
      (s) => s.circuitEscalatId === c.id || (c.perDefecte && s.circuitEscalatId == null),
    ).length;
  const tria = (id: number | 'nou' | null) => {
    if (id === sel) return;
    guarda(() => setSel(id));
  };

  return (
    <section className="pantalla" data-screen="W-25">
      <Toolbar titol={t('titols.circuits')}>
        <Permis recurs="cai.circuits" tipus="ALTA">
          <button type="button" className="btn btn--dark btn--s" onClick={() => tria('nou')}>
            <Icona nom="plus" />
            {t('circuits.nou')}
          </button>
        </Permis>
      </Toolbar>
      <div className="lf">
        <div className="lf__llista">
          <div className="lf__eines">
            <Cerca valor={cerca} onCanvia={setCerca} placeholder={t('avisos.circuits.cerca')} />
            <label className="switch-row t-footnote">
              {t('marc.mostraInactius')}
              <input
                type="checkbox"
                className="switch"
                checked={inactius}
                onChange={(e) => setInactius(e.target.checked)}
              />
            </label>
          </div>
          {circuits.isError ? (
            <ErrorCarrega onReintenta={() => void circuits.refetch()} />
          ) : visibles.length === 0 && sel !== 'nou' ? (
            <Buit icona="sliders">
              {circuits.isLoading
                ? t('comu.carregant')
                : q
                  ? t('avisos.senseResultats')
                  : t('circuits.buit')}
            </Buit>
          ) : (
            <ul className="lf__items" role="listbox" aria-label={t('circuits.llista')}>
              {sel === 'nou' ? (
                <li className="lf__item" role="option" aria-selected>
                  <Icona nom="plus" />
                  <span className="lf__text">
                    <span className="lf__nom">{t('circuits.nou')}</span>
                  </span>
                </li>
              ) : null}
              {visibles.map((c) => (
                <li
                  key={c.id}
                  role="option"
                  tabIndex={0}
                  className={`lf__item${c.actiu === false ? ' is-inactiu' : ''}`}
                  aria-selected={sel !== 'nou' && c.id === actiu?.id}
                  onClick={() => tria(c.id ?? null)}
                  onKeyDown={(e) => e.key === 'Enter' && tria(c.id ?? null)}
                >
                  <span className="lf__text">
                    <span className="lf__nom">{c.nom}</span>
                    <span className="lf__sub">
                      {nomServei(c.serveiId) ?? t('avisos.circuits.general')} ·{' '}
                      {t('circuits.resum', { count: c.nivells.length })}
                    </span>
                  </span>
                  {c.perDefecte ? <Icona nom="star" titol={t('circuits.marcaDefecte')} /> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        {actiu ? (
          <Editor
            key={actiu.id ?? 'nou'}
            inicial={actiu}
            usos={usos(actiu)}
            onCreat={(c) => {
              setCreat(c);
              setSel(c.id ?? null);
            }}
          />
        ) : (
          <div className="lf__fitxa">
            <Buit icona="sliders">
              {circuits.isLoading ? t('comu.carregant') : t('circuits.buit')}
            </Buit>
          </div>
        )}
      </div>
    </section>
  );
}
